function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable no-unused-vars */
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Close } from '../Close';
import { RadioButton } from '../RadioButton';
import { ENTER_KEY, SPACE_KEYCODE } from '../Utils/constant';
import moduleStyles from '@dtsl/css-card/dist/index.module.css';
import Tip from '@dtsl/icons/dist/icons/react/Tip';

// eslint-disable-next-line react/display-name
const CardWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    url = '#',
    styles = {},
    target = '_self',
    onClick = () => {},
    variant = '',
    disabled = false,
    children,
    className = '',
    direction = 'column',
    dataTestId = null,
    isSelected = false,
    percentage = 0,
    columnReverse = false,
    customCard = false,
    onRadioChange,
    radioSelect,
    radioId,
    checkedRadio,
    closeButtonProps = null,
    isTip = false,
    ...restProps
  } = props;
  const isClickable = variant === 'clickable';
  const nonLink = url === '#' || !url;
  {
    const cardClassName = classNames({
      [moduleStyles['sib-card']]: true,
      [moduleStyles['sib-card--radio']]: radioSelect,
      [moduleStyles['sib-card--clickable']]: isClickable,
      [moduleStyles['sib-card-selected']]: isSelected && nonLink,
      [moduleStyles['sib-card--custom']]: customCard,
      [moduleStyles['sib-card--tip']]: isTip,
      [moduleStyles['sib-card--closable']]: !!closeButtonProps,
      [className]: !!className
    });
    const gridStyles = {};
    if (!customCard && percentage >= 0) {
      if (direction === 'row') {
        gridStyles.gridTemplateRows = `${percentage}% ${100 - percentage}%`;
        gridStyles.gridTemplateColumns = '100%';
      } else {
        gridStyles.gridTemplateColumns = `${percentage}% ${100 - percentage}%`;
        gridStyles.gridTemplateRows = '100%';
        gridStyles.direction = columnReverse ? 'rtl' : 'ltr';
      }
    }
    const handleKeyPress = e => {
      if (e.key == ENTER_KEY || e.keyCode == SPACE_KEYCODE) {
        handleClick(e);
      }
    };
    const handleClick = e => {
      if (nonLink && radioSelect && radioId != checkedRadio) {
        onRadioChange(e, radioId); //  update checked Radio button state only if its not already selected
      }
      onClick(e);
    };
    const defaultCardStyles = {
      height: '227px',
      width: '400px'
    };
    const cardStyles = {
      ...(!customCard ? defaultCardStyles : {}),
      // Apply defaultCardStyles when customCard is false
      ...gridStyles,
      ...styles
    };
    if (isClickable) {
      if (url && url !== '#') {
        return /*#__PURE__*/React.createElement("a", _extends({
          rel: "noreferrer",
          target: target,
          disabled: disabled,
          onClick: handleClick,
          className: cardClassName,
          href: disabled ? null : url,
          role: disabled ? 'link' : null,
          style: cardStyles,
          "aria-disabled": disabled ? 'true' : null,
          "data-testid": dataTestId ? dataTestId : null,
          ref: ref
        }, restProps), React.Children.map(children, (child, index) => {
          return /*#__PURE__*/React.cloneElement(child, {
            key: index
          });
        }));
      } else {
        return /*#__PURE__*/React.createElement("div", _extends({}, restProps, {
          className: cardClassName,
          "data-testid": dataTestId ? dataTestId : null,
          disabled: disabled,
          "aria-disabled": disabled ? 'true' : null,
          onClick: handleClick,
          role: "button",
          onKeyDown: handleKeyPress,
          tabIndex: disabled ? -1 : 0,
          style: cardStyles,
          ref: ref
        }), React.Children.map(children, (child, index) => {
          return /*#__PURE__*/React.cloneElement(child, {
            key: index
          });
        }));
      }
    }
    return /*#__PURE__*/React.createElement("div", _extends({}, restProps, {
      className: cardClassName,
      "data-testid": dataTestId ? dataTestId : null,
      style: cardStyles,
      ref: ref
    }), React.Children.map(children, (child, index) => {
      return /*#__PURE__*/React.cloneElement(child, {
        key: index,
        ...(isTip && {
          isTip
        })
      });
    }));
  }
});
const Card = /*#__PURE__*/forwardRef(function Card(props, ref) {
  /* eslint-disable react/prop-types */
  const {
    children,
    radioSelect = false,
    checkedRadio = '',
    onRadioChange = () => {},
    radioMetaData = {},
    closeButtonProps = null,
    isTip = false,
    ...cardProps
  } = props;
  /* eslint-disable react/prop-types */

  const nonLink = cardProps?.url == '#' || !cardProps?.url;
  const absolutePositionStyles = {
    position: 'absolute',
    top: '1rem',
    right: '1rem'
  };
  const validChildren = [];
  const isClickableVariant = props.variant === 'clickable';
  const shouldRenderRadio = isClickableVariant && nonLink && radioSelect;
  const shouldRenderCloseButton = closeButtonProps && !isClickableVariant && !radioSelect && nonLink;

  // Conditionally add RadioButton if applicable
  if (shouldRenderRadio) {
    validChildren.push(/*#__PURE__*/React.createElement("span", {
      style: absolutePositionStyles,
      key: "radioButton"
    }, /*#__PURE__*/React.createElement(RadioButton, _extends({}, radioMetaData, {
      checked: checkedRadio == radioMetaData?.identifier,
      onChange: () => {},
      tabIndex: -1
    }))));
  }

  // Conditionally add Close button if applicable
  if (shouldRenderCloseButton) {
    validChildren.push(/*#__PURE__*/React.createElement("span", {
      style: absolutePositionStyles,
      key: "closeButton"
    }, /*#__PURE__*/React.createElement(Close, closeButtonProps)));
  }

  // Add the original children
  validChildren.push(...React.Children.toArray(children));
  return /*#__PURE__*/React.createElement(CardWrapper, _extends({}, cardProps, {
    radioSelect: radioSelect,
    checkedRadio: checkedRadio,
    onRadioChange: onRadioChange,
    radioId: radioMetaData?.identifier,
    closeButtonProps: closeButtonProps,
    isTip: isTip,
    ref: ref
  }), validChildren);
});
function CardImage(props) {
  const {
    children = null,
    className = '',
    isTip,
    ...restProps
  } = props;
  const imageClass = classNames({
    [moduleStyles['sib-card__image']]: true,
    [className]: !!className
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: imageClass
  }, restProps), children);
}
function CardContent(props) {
  const {
    className,
    children,
    isTip,
    ...restProps
  } = props;
  const cardContentClass = classNames({
    [moduleStyles['sib-card__content']]: true,
    [className]: !!className
  });
  if (Array.isArray(children)) {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: cardContentClass
    }, restProps), React.Children.map(children, (child, index) => {
      if (typeof child === 'string') {
        return child;
      } else if (/*#__PURE__*/React.isValidElement(child)) {
        return /*#__PURE__*/React.cloneElement(child, {
          key: index,
          ...(isTip && {
            isTip
          })
        });
      } else {
        return null; // as returning null is better then empty fragment
      }
    }));
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cardContentClass
  }, restProps), children);
}
function CardTitle(props) {
  const {
    className,
    children,
    isTip,
    ...restProps
  } = props;
  const titleClasses = classNames({
    [moduleStyles['sib-card__title--tip']]: isTip,
    [className]: !!className
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: titleClasses
  }, restProps), isTip ? /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Tip, null)) : null, children);
}
Card.Content = CardContent;
Card.Image = CardImage;
Card.Title = CardTitle;
CardContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};
Card.propTypes = {
  children: PropTypes.node.isRequired
};
CardImage.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};
CardWrapper.propTypes = {
  styles: PropTypes.object,
  url: PropTypes.string,
  content: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  variant: PropTypes.oneOf(['default', '', 'clickable']),
  children: PropTypes.node,
  direction: PropTypes.oneOf(['row', 'column']),
  columnReverse: PropTypes.bool,
  percentage: PropTypes.number,
  target: PropTypes.string,
  isSelected: PropTypes.bool,
  customCard: PropTypes.bool,
  radioSelect: PropTypes.bool,
  checkedRadio: PropTypes.string,
  radioId: PropTypes.string,
  onRadioChange: PropTypes.func,
  radioMetaData: PropTypes.object,
  closeButtonProps: PropTypes.object,
  isTip: PropTypes.bool
};
export default Card;